import React from 'react';
import LossbackPanel from './LossbackPanel';

// ============================================
// /lossback — the 10% Lossback page. Explains the rules and hosts the claim
// flow (LossbackPanel). Gated by the feature_lossback_enabled flag.
// Rules mirror the backend: $10K+ weighted wager this leaderboard period,
// 10% of deposit losses capped at LOSSBACK_MAX_PAYOUT ($2,000), one claim per
// period, submissions on the 17th–22nd (UK time).
// ============================================

const WINDOW_START = 17;
const WINDOW_END = 22;

// Claim window status from the UK calendar date (backend lossbackWindowOpen()
// is the source of truth; this only drives the status chip).
const getWindowStatus = () => {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'numeric', year: 'numeric' })
    .formatToParts(new Date())
    .reduce((acc, p) => ({ ...acc, [p.type]: parseInt(p.value, 10) }), {});
  const open = parts.day >= WINDOW_START && parts.day <= WINDOW_END;
  const monthIdx = parts.day > WINDOW_END ? parts.month : parts.month - 1; // 0-based month of the next window
  const monthName = new Date(Date.UTC(parts.year, monthIdx, 1)).toLocaleString('en-GB', { month: 'long', timeZone: 'UTC' });
  return { open, monthName };
};

const STEPS = [
  { n: '01', title: 'Wager $10K+', desc: <>Wager at least <strong>$10,000 weighted</strong> on Shuffle under guild <strong>SNUTZY</strong> during the leaderboard period (24th → 24th).</> },
  { n: '02', title: 'Get your P&L', desc: <>Ask Shuffle live support for your statistics from the 24th, or go to <strong>Transactions → Other → Request statistics</strong> and they'll be emailed to you.</> },
  { n: '03', title: 'Submit your claim', desc: <>Upload a screenshot of your P&amp;L on this page between the <strong>17th and 22nd</strong> of the month (UK time).</> },
  { n: '04', title: 'Get 10% back', desc: <>We check your claim against Shuffle's P&amp;L for the period and pay <strong>10% of your deposit losses</strong>, up to $2,000.</> }
];

export default function LossbackPage({ apiUrl, getToken, user, loginWithTwitch, enabled }) {
  if (!enabled) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
        <h1 className="page-title">10% Lossback</h1>
        <p className="page-subtitle">Coming soon — loyal guild SNUTZY players will get 10% back on their losses.</p>
      </div>
    );
  }

  const { open, monthName } = getWindowStatus();

  return (
    <div className="lb-page">
      <style>{`
        .lb-page { max-width: 900px; margin: 0 auto; }
        .lb-eyebrow { font-family: 'Space Mono', monospace; font-size: 0.68rem; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: #a78bfa; }
        .lb-panel {
          position: relative; overflow: hidden;
          background: linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015));
          border: 1px solid rgba(168,85,247,0.22); border-radius: 18px;
          padding: 1.5rem; margin-bottom: 1.5rem;
          box-shadow: 0 24px 60px -36px rgba(168,85,247,0.55), inset 0 1px 0 rgba(255,255,255,0.05);
        }
        .lb-panel::before {
          content: ''; position: absolute; inset: 0; pointer-events: none;
          background: radial-gradient(80% 120% at 0% 0%, rgba(168,85,247,0.14), transparent 60%);
        }
        .lb-panel > * { position: relative; }
        .lb-hero { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr); gap: 1.5rem; align-items: center; }
        .lb-big {
          font-family: 'Unbounded', 'Outfit', sans-serif; font-size: clamp(3.4rem, 9vw, 5rem); font-weight: 900; line-height: 1;
          letter-spacing: -0.03em; margin-top: 0.5rem;
          background: linear-gradient(135deg, #fff 30%, #d8b4fe 65%, #a855f7 95%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .lb-big-label { font-size: 0.85rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #e4e4e7; margin-top: 0.5rem; }
        .lb-window {
          display: inline-flex; align-items: center; gap: 0.45rem; margin-top: 1rem;
          padding: 0.4rem 0.8rem; border-radius: 999px; font-size: 0.78rem; font-weight: 700;
        }
        .lb-window.open { background: rgba(34,197,94,0.12); border: 1px solid rgba(34,197,94,0.35); color: #4ade80; }
        .lb-window.closed { background: rgba(168,85,247,0.1); border: 1px solid rgba(168,85,247,0.3); color: #d8b4fe; }
        .lb-dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; box-shadow: 0 0 10px currentColor; }
        .lb-facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.6rem; }
        .lb-fact { background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 0.85rem 1rem; }
        .lb-fact-value { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 1.2rem; font-weight: 800; color: #fff; white-space: nowrap; }
        .lb-fact-value.gold { background: linear-gradient(135deg, #fde68a, #fbbf24 45%, #f59e0b); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .lb-fact-label { font-size: 0.72rem; color: #a1a1aa; margin-top: 0.25rem; }
        .lb-h2 { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 1.15rem; font-weight: 800; color: #fff; margin: 0.4rem 0 1rem; }
        .lb-login { text-align: center; padding: 0.5rem 0; }
        .lb-login p { color: #a1a1aa; font-size: 0.9rem; margin: 0 0 1rem; }
        .lb-btn {
          display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.75rem 1.4rem; border: none; border-radius: 12px; cursor: pointer;
          background: linear-gradient(135deg, #8b5cf6, #a855f7); color: #fff; font-family: 'Outfit', sans-serif; font-size: 0.92rem; font-weight: 800;
          box-shadow: 0 10px 28px -12px rgba(168,85,247,0.75); transition: transform 0.2s ease;
        }
        .lb-btn:hover { transform: translateY(-2px); }
        .lb-btn:focus-visible { outline: 2px solid #d8b4fe; outline-offset: 2px; }
        .lb-steps { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.75rem; margin-bottom: 1.5rem; }
        .lb-step { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.07); border-radius: 14px; padding: 1rem 1.05rem; }
        .lb-step-n { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 0.8rem; font-weight: 800; color: #a78bfa; }
        .lb-step-title { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 0.86rem; font-weight: 700; color: #fff; margin: 0.4rem 0 0.3rem; }
        .lb-step-desc { font-size: 0.8rem; color: #a1a1aa; line-height: 1.5; }
        .lb-step-desc strong, .lb-rules strong { color: #e9d5ff; }
        .lb-rules { margin: 0; padding: 0; list-style: none; display: grid; gap: 0.55rem; }
        .lb-rules li { position: relative; padding-left: 1.2rem; font-size: 0.84rem; color: #a1a1aa; line-height: 1.55; }
        .lb-rules li::before { content: ''; position: absolute; left: 0; top: 0.55em; width: 6px; height: 6px; border-radius: 50%; background: #a855f7; }
        .lb-rules a { color: #c4b5fd; font-weight: 600; text-decoration: underline; text-underline-offset: 3px; }
        @media (max-width: 760px) {
          .lb-hero { grid-template-columns: 1fr; }
          .lb-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 480px) {
          .lb-panel { padding: 1.15rem; border-radius: 16px; }
          .lb-steps { grid-template-columns: 1fr; }
          .lb-fact-value { font-size: 1.05rem; }
        }
      `}</style>

      <h1 className="simple-page-title">10% Lossback</h1>
      <p className="rewards-intro">
        A rough month shouldn't cost you everything. Loyal guild <strong style={{ color: '#a855f7' }}>SNUTZY</strong> players get 10% of their losses back, every leaderboard period.
      </p>

      <div className="lb-panel">
        <div className="lb-hero">
          <div>
            <div className="lb-eyebrow">[ Every month ]</div>
            <div className="lb-big">10%</div>
            <div className="lb-big-label">of your losses back</div>
            <div className={`lb-window ${open ? 'open' : 'closed'}`}>
              <span className="lb-dot" aria-hidden="true" />
              {open
                ? `Claims are open now — until the ${WINDOW_END}nd`
                : `Next claim window: ${WINDOW_START}th–${WINDOW_END}nd ${monthName}`}
            </div>
          </div>
          <div className="lb-facts">
            <div className="lb-fact"><div className="lb-fact-value gold">$2,000</div><div className="lb-fact-label">Max payout per claim</div></div>
            <div className="lb-fact"><div className="lb-fact-value">$10K+</div><div className="lb-fact-label">Weighted wager this period</div></div>
            <div className="lb-fact"><div className="lb-fact-value">{WINDOW_START}th–{WINDOW_END}nd</div><div className="lb-fact-label">Claim window (UK time)</div></div>
            <div className="lb-fact"><div className="lb-fact-value">1 claim</div><div className="lb-fact-label">Per leaderboard period</div></div>
          </div>
        </div>
      </div>

      <div className="lb-panel">
        <div className="lb-eyebrow">[ Your claim ]</div>
        <h2 className="lb-h2">Claim your lossback</h2>
        {user ? (
          <LossbackPanel apiUrl={apiUrl} getToken={getToken} />
        ) : (
          <div className="lb-login">
            <p>Log in with Twitch and link your Shuffle username to check your eligibility and submit a claim.</p>
            <button className="lb-btn" onClick={loginWithTwitch}>Log in with Twitch</button>
          </div>
        )}
      </div>

      <div className="lb-eyebrow" style={{ marginBottom: '0.9rem' }}>[ How it works ]</div>
      <div className="lb-steps">
        {STEPS.map(s => (
          <div key={s.n} className="lb-step">
            <div className="lb-step-n">{s.n}</div>
            <div className="lb-step-title">{s.title}</div>
            <div className="lb-step-desc">{s.desc}</div>
          </div>
        ))}
      </div>

      <div className="lb-panel" style={{ marginBottom: '2.5rem' }}>
        <div className="lb-eyebrow">[ Good to know ]</div>
        <h2 className="lb-h2">The fine print</h2>
        <ul className="lb-rules">
          <li>Only losses on your own <strong>deposits</strong> count. Tips you receive are not included.</li>
          <li>Each claim covers the <strong>current leaderboard period</strong> and is checked against the P&amp;L Shuffle reports for that period, not the figure on your screenshot.</li>
          <li>Verifying with Shuffle takes time, so lossback pays out slower than other rewards.</li>
          <li>A denied claim doesn't use up your period. Fix the issue and submit again while the window is open.</li>
          <li>Falsified screenshots disqualify you from all rewards.</li>
          <li>Questions? Reach out to <a href="http://discord.com/users/182121536461864960" target="_blank" rel="noopener noreferrer">@pa1n</a> on Discord.</li>
        </ul>
      </div>
    </div>
  );
}
